import React, { useState, useEffect } from 'react';
import { ChevronDown, X, MessageSquare, Timer } from 'lucide-react';
import MacroManager, { defaultMacros } from './MacroManager';
import axios from '../../axios-config'; // added axios import

const HALL_PASS_OPTIONS = [
  { id: 'no-pass', label: 'No Pass', duration: 0 },
  { id: 'bathroom', label: 'Bathroom', duration: 5 },
  { id: 'back-to-class', label: 'Passport - Back To Class', duration: 4 },
  { id: 'attendance', label: 'Passport - To Attendance', duration: 4 },
  { id: 'counselor', label: 'Passport - To Counselor\'s Office', duration: 4 },
  { id: 'health', label: 'Passport - To Health Office', duration: 4 },
  { id: 'library', label: 'Passport - To Library', duration: 4 },
  { id: 'reception', label: 'Passport - To Reception', duration: 4 },
  { id: 'accountability', label: 'Passport - To Student Accountability Office', duration: 4 },
  { id: 'engagement', label: 'Passport - To Student Engagement Office', duration: 4 },
];

interface StudentCardProps {
  studentId: string;
  name: string;
  points: number;
  image: string;
  onClose?: () => void;
}

export default function StudentCard({ studentId, name, points, image, onClose }: StudentCardProps) {
  const [behaviorScore, setBehaviorScore] = useState(8);
  const [participationScore, setParticipationScore] = useState(7);
  const [hallPass, setHallPass] = useState(HALL_PASS_OPTIONS[0]);
  const [isHallPassOpen, setIsHallPassOpen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [timerState, setTimerState] = useState<'inactive' | 'active' | 'paused'>('inactive');
  const [notes, setNotes] = useState('');
  const [customPoints, setCustomPoints] = useState('');
  const [isMacroModalOpen, setIsMacroModalOpen] = useState(false);
  const [balance, setBalance] = useState(points); // new state for balance
  const [isImageModalOpen, setIsImageModalOpen] = useState(false); // new state for image modal

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (timerState === 'active' && timeRemaining !== null && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => prev !== null ? prev - 1 : null);
      }, 1000);
    } else if (timeRemaining === 0) {
      setTimerState('inactive');
      setTimeRemaining(null);
      setHallPass(HALL_PASS_OPTIONS[0]);
    }

    return () => clearInterval(interval);
  }, [timerState, timeRemaining]);

  // const startTimer = () => {
  //   if (hallPass.duration > 0) {
  //     setTimeRemaining(hallPass.duration * 60);
  //     setTimerState('active');
  //   }
  // };

  const pauseTimer = () => {
    setTimerState('paused');
  };

  const resumeTimer = () => {
    setTimerState('active');
  };

  const endHallPass = () => {
    setTimerState('inactive');
    setTimeRemaining(null);
    setHallPass(HALL_PASS_OPTIONS[0]);
  };

  // const stopTimer = () => {
  //   setTimerState('inactive');
  //   setTimeRemaining(null);
  // };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAddPoints = async (amount: number) => {
    try {
      const response = await axios.post(`/api/AcornTransaction?studentId=${studentId}&points=${amount}`);
      const newBalance = response.data;
      setBalance(newBalance);
      console.log(`New balance: ${newBalance}`);
    } catch (error: any) {
      console.error('Error adding points:', error.message);
    }
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setNotes(newValue);

    // Check if the last character typed is a space
    if (newValue.endsWith(' ')) {
      const words = newValue.split(' ');
      const lastWord = words[words.length - 2]; // Get the word before the space

      // Find matching macro
      const macro = defaultMacros.find(m => m.shortcut === lastWord);
      if (macro) {
        // Replace the shortcut with the macro text
        const withoutShortcut = newValue.slice(0, -(lastWord.length + 1));
        setNotes(withoutShortcut + macro.text + ' ');
      }
    }
  };

  const handleInsertMacro = (text: string) => {
    setNotes((prev) => prev + (prev ? '\n' : '') + text);
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4 relative z-50 max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <img
            src={image}
            alt={name}
            className="w-16 h-16 rounded-full object-cover cursor-pointer"
            onClick={() => setIsImageModalOpen(true)}
          />
          <div>
            <h2 className="text-2xl font-bold">{name}</h2>
            <p className="text-gray-600">{balance} Points</p> {/* use balance */}
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X size={24} />
        </button>
      </div>

      {/* Behavior Score */}
      {import.meta.env.VITE_ACORN_EDITION === 'full' && (
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Behavior Score</h3>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">-10</span>
          <input
            type="range"
            min="-10"
            max="10"
            step="1"
            value={behaviorScore}
            onChange={(e) => setBehaviorScore(Number(e.target.value))}
            className="flex-1 h-2 bg-gradient-to-r from-red-200 via-gray-200 to-green-200 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500"
          />
          <span className="text-sm text-gray-500">+10</span>
          <span className={`text-2xl font-semibold ${
            behaviorScore > 0 ? 'text-green-600' : behaviorScore < 0 ? 'text-red-600' : 'text-gray-600'
          }`}>{behaviorScore > 0 ? '+' : ''}{behaviorScore}</span>
        </div>
      </div>
      )}
      {/* Participation Score */}
      {import.meta.env.VITE_ACORN_EDITION === 'full' && (
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Participation Score</h3>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">-10</span>
          <input
            type="range"
            min="-10"
            max="10"
            step="1"
            value={participationScore}
            onChange={(e) => setParticipationScore(Number(e.target.value))}
            className="flex-1 h-2 bg-gradient-to-r from-red-200 via-gray-200 to-green-200 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500"
          />
          <span className="text-sm text-gray-500">+10</span>
          <span className={`text-2xl font-semibold ${
            participationScore > 0 ? 'text-green-600' : participationScore < 0 ? 'text-red-600' : 'text-gray-600'
          }`}>{participationScore > 0 ? '+' : ''}{participationScore}</span>
        </div>
      </div>
      )}
      {/* Hall Pass */}
      {import.meta.env.VITE_ACORN_EDITION === 'full' && (
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Hall Pass</h3>
        <div className="space-y-3">
          <div className="relative w-full">
            <button
              className="w-full px-4 py-3 text-left border rounded-lg flex items-center justify-between bg-white hover:bg-gray-50"
              onClick={() => setIsHallPassOpen(!isHallPassOpen)}
              disabled={timerState !== 'inactive'}
            >
              <div className="flex items-center gap-3">
                <Timer size={20} className="text-gray-500" />
                <div>
                  <div className="font-medium">{hallPass.label}</div>
                  {hallPass.duration > 0 && (
                    <div className="text-sm text-gray-500">{hallPass.duration} mins</div>
                  )}
                </div>
              </div>
              <ChevronDown 
                size={20} 
                className={`text-gray-500 transition-transform ${isHallPassOpen ? 'rotate-180' : ''}`} 
              />
            </button>

            {isHallPassOpen && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setIsHallPassOpen(false)}
                />
                <div className="absolute z-20 mt-2 w-full bg-white rounded-lg border shadow-lg py-1 max-h-[300px] overflow-y-auto">
                  {HALL_PASS_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3
                        ${hallPass.id === option.id ? 'bg-blue-50' : ''}`}
                      onClick={() => {
                        setHallPass(option);
                        setIsHallPassOpen(false);
                       if (option.duration > 0) {
                         setTimeRemaining(option.duration * 60);
                         setTimerState('active');
                       }
                      }}
                    >
                      <Timer size={20} className="text-gray-500" />
                      <div>
                        <div className="font-medium">{option.label}</div>
                        {option.duration > 0 && (
                          <div className="text-sm text-gray-500">{option.duration} mins</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {hallPass.duration > 0 && (
            <div className="flex items-center gap-2">
              <div className={`flex-1 py-2 px-4 rounded-lg font-medium text-center ${
                timerState === 'paused' ? 'bg-yellow-100' : 'bg-gray-100'
              }`}>
                {timeRemaining !== null && formatTime(timeRemaining)}
              </div>
              {timerState === 'active' && (
                <button
                  onClick={pauseTimer}
                  className="py-2 px-4 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-medium"
                >
                  Pause
                </button>
              )}
              {timerState === 'paused' && (
                <button
                  onClick={resumeTimer}
                  className="py-2 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium"
                >
                  Resume
                </button>
              )}
              {timerState !== 'inactive' && (
                <button
                  onClick={endHallPass}
                  className="py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium"
                >
                  End Pass
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      )}
      {/* Notes */}
      {import.meta.env.VITE_ACORN_EDITION === 'full' && (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Notes</h3>
          <button
            onClick={() => setIsMacroModalOpen(true)}
            className="flex items-center gap-2 text-blue-500 hover:text-blue-700"
          >
            <MessageSquare size={18} />
            Manage Macros
          </button>
        </div>
        <textarea
          value={notes}
          onChange={handleNotesChange}
          placeholder="Add notes about the student..."
          className="w-full p-3 border rounded-lg h-32 resize-none"
        />
        {isMacroModalOpen && (
          <MacroManager
            onInsert={(text) => {
              handleInsertMacro(text);
              setIsMacroModalOpen(false);
            }}
            onClose={() => setIsMacroModalOpen(false)}
          />
        )}
      </div>
      )}
      {/* Points Management */}
      <div>
        <h3 className="text-xl font-semibold mb-4">Points Management</h3>
        <p className="text-gray-600 mb-4">Current Balance: {balance}</p> {/* use balance */}
        
        {/* Quick Add Buttons */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          {[1].map((value) => (
            <button
              key={value}
              onClick={() => handleAddPoints(value)}
              className="py-2 px-4 bg-gray-100 rounded-lg hover:bg-gray-200 font-semibold"
            >
              +{value}
            </button>
          ))}
        </div>

        {/* Custom Amount */}
        <div className="flex gap-4 hidden">
          <input
            type="number"
            value={customPoints}
            onChange={(e) => setCustomPoints(e.target.value)}
            placeholder="Enter custom amount..."
            className="flex-1 p-3 border rounded-lg"
          />
          <button
            onClick={() => {
              if (customPoints) {
                handleAddPoints(Number(customPoints));
                setCustomPoints('');
              }
            }}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold"
          >
            Add Points
          </button>
        </div>
      </div>
      </div>
      <div className="bg-white p-4 rounded shadow-md">
        <h2 className="text-lg font-bold">{name}</h2>
        <p>Points: {balance}</p> {/* use balance */}
      </div>
      
      {isImageModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white p-4 rounded-lg relative">
            <button
              title="Close"
              onClick={() => setIsImageModalOpen(false)}
              className="absolute top-2 right-2 p-1 transition-colors hover:bg-gray-100 rounded-full"
            >
              <X size={24} />
            </button>
            <img
              src={image}
              alt={name}
              className="max-w-full max-h-[90vh] object-cover"
            />
          </div>
        </div>
      )}
    </div>
  );
}